import { useState, useRef } from 'react';
import { IconArrowLeft, IconFileImport, IconAlertTriangle, IconCheck, IconX } from '@tabler/icons-react';
import { useProducts } from '../context/ProductsContext';
import { parseEtsyCsv } from '../lib/etsyCsv';
import { findMatch, guessCategory, importListings, summaryText, CATEGORIES } from '../lib/etsyImport';
import { ensureCategoryPages, categoryPagesText } from '../lib/categoryPages';
import { useSiteContent, useCollectionPages } from '../context/SiteContentContext';
import { withIds } from './AdminPages';
import { MAX_PRODUCT_IMAGES } from '../lib/imageStore';
import { showToast } from '../components/ui/Toast';

function readFileText(file) {
  return new Promise(function(resolve, reject) {
    var reader = new FileReader();
    reader.onload = function() { resolve(String(reader.result || '')); };
    reader.onerror = function() { reject(reader.error); };
    reader.readAsText(file);
  });
}

var th = { padding: '10px 12px', textAlign: 'left', fontWeight: 500 };
var td = { padding: '10px 12px', verticalAlign: 'top' };

// The "Import from Etsy CSV" screen, opened from Products.
// props.onClose() goes back to the product list.
export default function EtsyImport(props) {
  var ctx = useProducts();
  var fileInput = useRef(null);
  var [stage, setStage] = useState('pick'); // pick | preview | importing | done
  var [fileName, setFileName] = useState('');
  var [parsed, setParsed] = useState(null);
  var [ticked, setTicked] = useState({});
  var [progress, setProgress] = useState({ done: 0, total: 0, name: '' });
  var [results, setResults] = useState([]);
  var [pagesNote, setPagesNote] = useState(null); // { text, error }
  var collectionPages = useCollectionPages().pages;
  var saveContent = useSiteContent().save;

  var offline = !!ctx.loadError;

  async function handleFile(e) {
    var file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.csv$/i.test(file.name) && file.type !== 'text/csv') {
      showToast('Please choose the .csv file you downloaded from Etsy.', 'error');
      return;
    }
    var text;
    try {
      text = await readFileText(file);
    } catch (err) {
      showToast('That file could not be read. Please try downloading it from Etsy again.', 'error');
      return;
    }
    var res = parseEtsyCsv(text);
    var initial = {};
    res.listings.forEach(function(l, i) {
      var m = findMatch(l, ctx.allProducts);
      initial[i] = l.currency === 'USD' && !m.error && !(m.sameName && !m.product);
    });
    setFileName(file.name);
    setParsed(res);
    setTicked(initial);
    setStage('preview');
  }

  function toggle(i) {
    setTicked(function(prev) { return Object.assign({}, prev, { [i]: !prev[i] }); });
  }

  function toggleAll(on) {
    var next = {};
    parsed.listings.forEach(function(_, i) { next[i] = on; });
    setTicked(next);
  }

  async function runImport() {
    var chosen = parsed.listings.filter(function(_, i) { return ticked[i]; });
    if (!chosen.length) return;
    setStage('importing');
    setProgress({ done: 0, total: chosen.length, name: '' });
    var res = await importListings(chosen, {
      products: ctx.allProducts,
      saveProduct: ctx.saveProduct,
      onProgress: function(done, total, listing) {
        setProgress({ done: done, total: total, name: listing ? listing.name : '' });
      },
    });
    var note = await addCategoryPages();
    setResults(res);
    setPagesNote(note);
    setStage('done');
    var anyFailed = res.some(function(r) { return r.outcome === 'failed'; });
    showToast(summaryText(res) + (note ? ' ' + note.text : ''), anyFailed || (note && note.error) ? 'error' : undefined);
  }

  // Every category gets its own shop page, so imported pieces show up
  // under Shop as soon as they're published. Returns null when nothing changed.
  async function addCategoryPages() {
    var pagesRes = ensureCategoryPages(withIds(collectionPages || []), CATEGORIES);
    if (!pagesRes.added.length && !pagesRes.linked.length) return null;
    var saved = await saveContent({ collection_pages: pagesRes.pages });
    if (saved && saved.error) return { text: 'The new shop pages could not be saved: ' + saved.error, error: true };
    return { text: 'Shop pages: ' + categoryPagesText(pagesRes), error: false };
  }

  var header = (
    <div>
      {stage !== 'importing' && (
        <button className="adm-btn adm-btn--outline" onClick={props.onClose} style={{ marginBottom: 16 }}>
          <IconArrowLeft size={14} /> Back to products
        </button>
      )}
      <h1 className="adm-title">Import from Etsy</h1>
    </div>
  );

  if (stage === 'pick') {
    return (
      <div>
        {header}
        <p className="adm-sub">Bring your Etsy listings into the shop. They arrive as Drafts, so nothing shows to customers until you publish it.</p>
        {offline && (
          <div className="adm-card" style={{ background: '#FFF4E5', color: '#7A4B00', fontSize: 13 }}>
            Not connected to the database, so nothing can be imported right now. Check your internet connection and reload the page.
          </div>
        )}
        <div className="adm-card">
          <div className="adm-card-title">1. Download your listings from Etsy</div>
          <p style={{ fontSize: 13, color: '#555', lineHeight: 1.6, margin: 0 }}>
            In Etsy go to <strong>Shop Manager &gt; Settings &gt; Options &gt; Download Data</strong>, and under
            {' '}<strong>Currently for sale listings</strong> click <strong>Download CSV</strong>.
          </p>
        </div>
        <div className="adm-card">
          <div className="adm-card-title">2. Choose that file</div>
          <p style={{ fontSize: 13, color: '#555', lineHeight: 1.6, marginTop: 0 }}>
            You’ll see every listing first and tick the ones to bring in. Listings already in the shop are updated, not copied twice.
          </p>
          <input ref={fileInput} type="file" accept=".csv,text/csv" onChange={handleFile} style={{ display: 'none' }} data-testid="etsy-file" />
          <button className="adm-btn" disabled={offline} style={{ opacity: offline ? 0.6 : 1 }} onClick={function() { fileInput.current.click(); }}>
            <IconFileImport size={14} /> Choose Etsy CSV file
          </button>
        </div>
      </div>
    );
  }

  if (stage === 'importing') {
    var pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
    return (
      <div>
        {header}
        <div className="adm-card">
          <div className="adm-card-title">{'Importing ' + Math.min(progress.done + 1, progress.total) + ' of ' + progress.total + '…'}</div>
          <p style={{ fontSize: 13, color: '#555', margin: '0 0 10px' }}>{progress.name ? progress.name + ' (copying photos from Etsy can take a few seconds)' : 'Finishing…'}</p>
          <div style={{ height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: pct + '%', height: '100%', background: 'var(--color-primary, #1E3A2F)', transition: 'width 0.3s' }} />
          </div>
          <p style={{ fontSize: 11, color: '#888', marginTop: 10, marginBottom: 0 }}>Please keep this page open until it finishes.</p>
        </div>
      </div>
    );
  }

  if (stage === 'done') {
    return (
      <div>
        {header}
        <p className="adm-sub">{summaryText(results)} New products are Drafts: open each one in Products to check it, then Publish.</p>
        {pagesNote && (
          <p className="adm-sub" data-testid="etsy-pages-note" style={{ color: pagesNote.error ? '#B42318' : '#1E6B3A' }}>{pagesNote.text}</p>
        )}
        <div className="adm-card" style={{ padding: 0, overflow: 'hidden' }}>
          {results.map(function(r, i) {
            var ok = r.outcome !== 'failed';
            return (
              <div key={i} style={{ padding: '12px 14px', borderBottom: '1px solid rgba(74,46,26,0.08)', display: 'flex', gap: 10 }}>
                <div style={{ color: ok ? '#1E6B3A' : '#B42318', flexShrink: 0, paddingTop: 1 }}>{ok ? <IconCheck size={16} /> : <IconX size={16} />}</div>
                <div style={{ fontSize: 13 }}>
                  <div style={{ fontWeight: 500 }}>
                    {r.listing.name}
                    <span style={{ fontWeight: 400, color: ok ? '#1E6B3A' : '#B42318' }}>
                      {r.outcome === 'created' ? ' — added as a Draft' : r.outcome === 'updated' ? ' — updated your shop product “' + r.productName + '”' : ' — not imported'}
                    </span>
                  </div>
                  {r.error && <div style={{ color: '#B42318', marginTop: 3 }}>{r.error}</div>}
                  {r.notes.length > 0 && (
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: '#666', fontSize: 12, lineHeight: 1.6 }}>
                      {r.notes.map(function(n, j) { return <li key={j}>{n}</li>; })}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="adm-btn" onClick={props.onClose}>Back to products</button>
          <button className="adm-btn adm-btn--outline" onClick={function() { setStage('pick'); }}>Import another file</button>
        </div>
      </div>
    );
  }

  // preview
  var listings = parsed.listings;
  var count = listings.filter(function(_, i) { return ticked[i]; }).length;
  var allOn = listings.length > 0 && count === listings.length;
  return (
    <div>
      {header}
      <p className="adm-sub">{fileName + ': ' + listings.length + ' listing' + (listings.length !== 1 ? 's' : '') + ' found. Tick the ones to bring in.'}
        {' Updating a product takes its name, price and stock from Etsy; its photos, status, category and any colours or sizes only in the shop are kept.'}</p>

      {parsed.problems.length > 0 && (
        <div className="adm-card" style={{ background: '#FFF8EC', borderColor: '#F0D9A8' }}>
          <div className="adm-card-title" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#7A4B00' }}>
            <IconAlertTriangle size={15} /> {'Things to know (' + parsed.problems.length + ')'}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#5C4300', lineHeight: 1.7 }}>
            {parsed.problems.map(function(p, i) { return <li key={i}>{p}</li>; })}
          </ul>
        </div>
      )}

      {listings.length > 0 && (
        <div className="adm-card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'rgba(74,46,26,0.04)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#888' }}>
                <th style={th}>
                  <input type="checkbox" aria-label="Tick all listings" checked={allOn} onChange={function() { toggleAll(!allOn); }} style={{ accentColor: '#1E3A2F' }} />
                </th>
                <th style={th}>Listing</th>
                <th style={th}>Category</th>
                <th style={th}>Price</th>
                <th style={th}>Stock</th>
                <th style={th}>Photos</th>
                <th style={th}>In the shop</th>
              </tr>
            </thead>
            <tbody>
              {listings.map(function(l, i) {
                var match = findMatch(l, ctx.allProducts);
                var photos = l.imageUrls.length;
                var guess = guessCategory(l.name, l.tags);
                return (
                  <tr key={i} onClick={function() { toggle(i); }} style={{ borderBottom: '1px solid rgba(74,46,26,0.08)', cursor: 'pointer', background: ticked[i] ? 'transparent' : 'rgba(0,0,0,0.02)' }}>
                    <td style={td}>
                      <input type="checkbox" aria-label={'Import ' + l.name} checked={!!ticked[i]} onClick={function(e) { e.stopPropagation(); }} onChange={function() { toggle(i); }} style={{ accentColor: '#1E3A2F' }} />
                    </td>
                    <td style={Object.assign({}, td, { display: 'flex', gap: 10, minWidth: 220 })}>
                      <div style={{ width: 40, height: 40, borderRadius: 6, background: '#E0D9CC', overflow: 'hidden', flexShrink: 0 }}>
                        {photos > 0 && <img src={l.imageUrls[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                      </div>
                      <div>
                        <div style={{ fontWeight: 500 }}>{l.name}</div>
                        <div style={{ fontSize: 11, color: '#888' }}>{(l.sku ? 'SKU ' + l.sku : 'No SKU') + (l.etsyListingId ? ' · Etsy #' + l.etsyListingId : '')}</div>
                        {l.problems.map(function(p, j) {
                          return <div key={j} style={{ fontSize: 11, color: '#9A5B00', marginTop: 3 }}>{p}</div>;
                        })}
                      </div>
                    </td>
                    <td style={Object.assign({}, td, { whiteSpace: 'nowrap' })} data-testid={'etsy-category-' + i}>
                      {match.product
                        ? <span>{match.product.category || 'Other'}<div style={{ fontSize: 10, color: '#888' }}>yours, kept</div></span>
                        : guess === 'Other'
                          ? <span style={{ color: '#9A5B00' }}>Other<div style={{ fontSize: 10, color: '#888' }}>pick one after import</div></span>
                          : <span>{guess}<div style={{ fontSize: 10, color: '#888' }}>guessed</div></span>}
                    </td>
                    <td style={Object.assign({}, td, { whiteSpace: 'nowrap' })}>
                      {(l.currency === 'USD' ? '$' : l.currency + ' ') + l.price.toFixed(2)}
                      {l.currency !== 'USD' && <div style={{ fontSize: 10, color: '#9A5B00' }}>Not USD</div>}
                    </td>
                    <td style={td}>{l.quantity}</td>
                    <td style={Object.assign({}, td, { whiteSpace: 'nowrap' })}>
                      {photos > MAX_PRODUCT_IMAGES ? photos + ' (first 6 kept)' : photos}
                    </td>
                    <td style={Object.assign({}, td, { fontSize: 12 })}>
                      {match.error
                        ? <span style={{ color: '#B42318' }}>{match.error}</span>
                        : match.product
                          ? <span>{'Updates “' + match.product.name + '”'}<div style={{ fontSize: 10, color: '#888' }}>{match.by === 'etsy' ? 'same Etsy listing' : 'same SKU'}</div></span>
                          : match.sameName
                            ? <span style={{ color: '#9A5B00' }}>{'New draft, but “' + match.sameName.name + '” is already in the shop'}<div style={{ fontSize: 10, color: '#888' }}>tick only if it’s a different bag</div></span>
                            : <span style={{ color: '#1E6B3A' }}>New draft</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="adm-btn" disabled={!count || offline} onClick={runImport} style={{ opacity: !count || offline ? 0.6 : 1 }}>
          <IconFileImport size={14} /> {count ? 'Import ' + count + ' listing' + (count !== 1 ? 's' : '') : 'Tick a listing to import'}
        </button>
        <button className="adm-btn adm-btn--outline" onClick={function() { setStage('pick'); }}>Choose a different file</button>
      </div>
    </div>
  );
}
